import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useLanguage } from "../LanguageContext";
import translations from "../translation_projectdetail";

function ProjectDetail({ setShowHero }) {
  const { language } = useLanguage();
  const { id } = useParams();

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    setShowHero(false);
    return () => {
      setShowHero(true);
    };
  }, [setShowHero, id]); 

  const projects = {
    artfolio: {
      title: translations[language].title_artfolio,
      description: translations[language].description_artfolio,
      technologies: ["Dart", "Flutter", "Firebase", "Figma"],
      image: "/images/artfolio.png",
      category: "Mobile App",
      status: "DEPLOYED",
      year: "2024",
      display: [
        "/images/artfolio/getstarted_page.png",
        "/images/artfolio/signin_page.png",
        "/images/artfolio/discover_page.png",
        "/images/artfolio/detailpost_page.png",
        "/images/artfolio/explore_page.png",
        "/images/artfolio/upload_page.png",
        "/images/artfolio/form_page.png",
        "/images/artfolio/yourprofile_page.png",
      ],
    },
    pmr: {
      title: translations[language].title_pmr,
      description: translations[language].description_pmr,
      technologies: [
        "PHP",
        "JavaScript",
        "Laravel",
        "Node.js",
        "Tailwind CSS",
        "Figma",
      ],
      image: "/images/pmr.png",
      category: "Web App",
      status: "ACTIVE",
      year: "2024",
      display: [
        "/images/pmr/landing.png",
        "/images/pmr/signin.png",
        "/images/pmr/dashboard.png",
      ],
    },
    twodn: {
      title: translations[language].title_2dn,
      description: translations[language].description_2dn,
      technologies: [
        "PHP",
        "JavaScript",
        "CSS",
        "Node.js",
        "Bootstrap",
        "Figma",
      ],
      image: "/images/2dn.png",
      category: "E-Commerce",
      status: "ARCHIVED",
      year: "2023",
      display: ["/images/2dn/demo.gif"],
    },
    etraining: {
      title: translations[language].title_etraining,
      description: translations[language].description_etraining,
      technologies: ["PHP", "JavaScript", "Laravel", "Node.js", "Tailwind CSS"],
      image: "/images/etraining.png",
      category: "LMS Platform",
      status: "DEPLOYED",
      year: "2024",
      display: [
        "/images/etraining/login.png",
        "/images/etraining/tambah.png",
        "/images/etraining/tambah_kompetensi.png",
      ],
    },
  };

  const project = projects[id];

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#021327]">
        <div className="text-center font-mono">
          <div className="text-6xl text-red-400 mb-4">404</div>
          <div className="text-cyan-400 mb-2">PROJECT.NOT_FOUND</div>
          <div className="text-gray-400 text-sm">The requested project does not exist in our database.</div>
          <Link 
            to="/" 
            className="inline-flex items-center mt-4 text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            <span className="mr-2">← RETURN.HOME</span>
          </Link>
        </div>
      </div>
    );
  }

  // Custom arrow components for slider
  const NextArrow = ({ onClick }) => (
    <button
      onClick={onClick}
      className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-slate-900/80 border border-cyan-400/30 rounded-full flex items-center justify-center hover:border-cyan-400/70 hover:bg-slate-800/80 transition-all duration-300 group"
    >
      <svg className="w-5 h-5 text-cyan-400 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );

  const PrevArrow = ({ onClick }) => (
    <button
      onClick={onClick}
      className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-slate-900/80 border border-cyan-400/30 rounded-full flex items-center justify-center hover:border-cyan-400/70 hover:bg-slate-800/80 transition-all duration-300 group"
    >
      <svg className="w-5 h-5 text-cyan-400 group-hover:-translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
      </svg>
    </button>
  );

  const settings = {
    dots: true,
    infinite: project.display.length > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: project.display.length > 1,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    dotsClass: "slick-dots custom-dots",
    customPaging: (i) => (
      <button className="w-2 h-2 rounded-full bg-slate-600 hover:bg-cyan-400 transition-colors duration-300"></button>
    ),
  };

  const statusColors = {
    DEPLOYED: "text-green-400 border-green-400/30 bg-green-400/10",
    ACTIVE: "text-blue-400 border-blue-400/30 bg-blue-400/10",
    ARCHIVED: "text-yellow-400 border-yellow-400/30 bg-yellow-400/10",
  };

  return (
    <div className="min-h-screen bg-[#021327] relative overflow-hidden">
      {/* Grid Background */}
      <div className="fixed inset-0 grid-plus-background opacity-5 z-0"></div>
      
      {/* HUD Overlay Elements */}
      <div className="fixed inset-0 pointer-events-none z-10">
        {/* Corner Brackets */}
        <div className="absolute top-4 left-4 w-8 h-8 border-t-2 border-l-2 border-cyan-400/40"></div>
        <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-cyan-400/40"></div>
        <div className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-cyan-400/40"></div>
        <div className="absolute bottom-4 right-4 w-8 h-8 border-b-2 border-r-2 border-cyan-400/40"></div>
        
        {/* Status Lines */}
        <div className="absolute top-0 left-1/4 w-px h-16 bg-gradient-to-b from-cyan-400/30 to-transparent"></div>
        <div className="absolute top-0 right-1/4 w-px h-16 bg-gradient-to-b from-cyan-400/30 to-transparent"></div>
      </div>

      <div className="relative z-20 min-h-screen">
        {/* Header Navigation */}
        <header className="sticky top-0 z-30 bg-slate-900/95 border-b border-cyan-400/20 backdrop-blur-sm">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Link 
                to="/" 
                className="flex items-center text-cyan-400 hover:text-cyan-300 transition-colors duration-300 group font-mono"
              >
                <svg className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                  <path d="m3.55 12l7.35 7.35q.375.375.363.875t-.388.875t-.875.375t-.875-.375l-7.7-7.675q-.3-.3-.45-.675T.825 12t.15-.75t.45-.675l7.7-7.7q.375-.375.888-.363t.887.388t.375.875t-.375.875z" />
                </svg>
                <span className="text-sm uppercase tracking-wider">SYSTEM.RETURN</span>
              </Link>
              
              <div className="flex items-center space-x-4 font-mono text-xs">
                <div className="flex items-center text-cyan-400">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse mr-2"></div>
                  <span>PROJECT.ACTIVE</span>
                </div>
                <div className="text-gray-400">ID: {id.toUpperCase()}</div>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="container mx-auto px-6 py-8">
          {/* Project Header */}
          <div className="mb-8">
            <div className="bg-gradient-to-br from-slate-900/80 to-slate-800/60 border border-slate-700/50 rounded-xl p-8 backdrop-blur-sm relative">
              {/* HUD Corner Elements */}
              <div className="absolute top-3 left-3 w-4 h-4 border-l-2 border-t-2 border-cyan-400/60"></div>
              <div className="absolute top-3 right-3 w-4 h-4 border-r-2 border-t-2 border-cyan-400/60"></div>
              <div className="absolute bottom-3 left-3 w-4 h-4 border-l-2 border-b-2 border-cyan-400/60"></div>
              <div className="absolute bottom-3 right-3 w-4 h-4 border-r-2 border-b-2 border-cyan-400/60"></div>

              {/* Project Meta */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className="px-3 py-1 bg-cyan-500/10 border border-cyan-500/30 rounded-full">
                    <span className="text-xs font-mono uppercase tracking-wider text-cyan-300">
                      {project.category}
                    </span>
                  </div>
                  <div className={`px-3 py-1 border rounded-full ${statusColors[project.status]}`}>
                    <span className="text-xs font-mono uppercase tracking-wider">
                      {project.status}
                    </span>
                  </div>
                </div>
                <div className="text-cyan-400 font-mono text-sm">
                  YEAR: {project.year}
                </div>
              </div>

              {/* Project Title */}
              <h1 className="text-4xl font-bold text-white mb-4 font-mono">
                <span className="text-cyan-400">[</span>
                {project.title}
                <span className="text-cyan-400">]</span>
              </h1>

              {/* Project Description */}
              <p className="text-gray-300 text-lg leading-relaxed mb-6 font-mono">
                <span className="text-cyan-400 opacity-70">&gt;</span> {project.description}
              </p>

              {/* Tech Stack */}
              <div className="space-y-3">
                <div className="flex items-center">
                  <span className="text-cyan-400 text-sm font-mono uppercase tracking-wider mr-3">
                    Tech Stack:
                  </span>
                  <div className="flex-1 h-px bg-gradient-to-r from-cyan-500/30 to-transparent"></div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {project.technologies.map((tech, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 text-xs font-medium bg-slate-800/60 text-cyan-300 border border-slate-600/50 rounded-full hover:border-cyan-500/50 hover:bg-slate-700/60 transition-colors duration-300 font-mono"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Project Gallery */}
          <div className="mb-8">
            <div className="bg-gradient-to-br from-slate-900/80 to-slate-800/60 border border-slate-700/50 rounded-xl p-6 backdrop-blur-sm relative">
              {/* Section Header */}
              <div className="flex items-center mb-6">
                <div className="flex items-center text-cyan-400 font-mono text-sm uppercase tracking-wider">
                  <span className="w-2 h-2 bg-cyan-400 rounded-full mr-2 animate-pulse"></span>
                  <span className="opacity-70">PROJECT.GALLERY</span>
                </div>
                <div className="flex-1 h-px bg-gradient-to-r from-cyan-400/30 to-transparent ml-4"></div>
              </div>

              {/* Image Slider */}
              <div className="relative rounded-lg overflow-hidden border border-slate-600/50">
                <Slider {...settings}>
                  {project.display.map((preview, index) => (
                    <div key={index} className="relative">
                      <div className="aspect-video bg-slate-800 flex items-center justify-center">
                        <img
                          src={preview}
                          alt={`${project.title} screenshot ${index + 1}`}
                          className={`w-full h-full object-contain ${
                            id === "artfolio" ? "max-h-[450px]" : ""
                          }`}
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                      
                      {/* Image Overlay Info */}
                      <div className="absolute bottom-4 left-4 bg-slate-900/80 border border-cyan-400/30 rounded px-3 py-1 backdrop-blur-sm">
                        <span className="text-xs font-mono text-cyan-300">
                          IMG_{String(index + 1).padStart(2, '0')} / {String(project.display.length).padStart(2, '0')}
                        </span>
                      </div>
                    </div>
                  ))}
                </Slider>
              </div>
            </div>
          </div>

          {/* Project Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-slate-900/80 to-slate-800/60 border border-slate-700/50 rounded-xl p-6 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-cyan-400 font-mono text-sm">IMAGES</span>
                <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
              </div>
              <div className="text-2xl font-bold text-white font-mono">
                {String(project.display.length).padStart(2, '0')}
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-slate-900/80 to-slate-800/60 border border-slate-700/50 rounded-xl p-6 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-cyan-400 font-mono text-sm">TECH.COUNT</span>
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
              </div>
              <div className="text-2xl font-bold text-white font-mono">
                {String(project.technologies.length).padStart(2, '0')}
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-slate-900/80 to-slate-800/60 border border-slate-700/50 rounded-xl p-6 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-cyan-400 font-mono text-sm">STATUS</span>
                <div className={`w-2 h-2 rounded-full animate-pulse ${
                  project.status === 'DEPLOYED' ? 'bg-green-400' :
                  project.status === 'ACTIVE' ? 'bg-blue-400' : 'bg-yellow-400'
                }`}></div>
              </div>
              <div className="text-lg font-bold text-white font-mono">
                {project.status}
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Custom Styles for Slider */}
      <style jsx>{`
        .custom-dots {
          bottom: -50px !important;
          display: flex !important;
          justify-content: center;
          gap: 8px;
        }
        .custom-dots li {
          margin: 0 !important;
        }
        .custom-dots li button {
          opacity: 0.5;
        }
        .custom-dots li.slick-active button {
          opacity: 1;
          background-color: rgb(34 211 238) !important;
        }
        .slick-slide {
          padding: 0 4px;
        }
      `}</style>
    </div>
  );
}

export default ProjectDetail;